import { Button } from '@mui/material';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import { toast } from 'react-toastify';
import * as XLSX from 'xlsx';
import { getItemPOList, getItemPRList } from '../../api';
import StyledButton from '../../components/Common/StyledButton';
import { AmountCell } from '../../components/CustomComponents/AmountCell';
import DateFilter from '../../components/CustomComponents/DateFilter';
import { setBreadcrumbItems } from '../../store/actions';
import Table from '../DummyTable/Table';
import './styles/reports.scss';

const defaultPageSize = 10;

const getDefaultYearDateRange = () => {
  const now = new Date();
  const start = new Date(now.getFullYear(), 0, 1);
  start.setHours(0, 0, 0, 0);

  const end = new Date(now);
  end.setHours(23, 59, 59, 999);

  return {
    start: start.toISOString(),
    end: end.toISOString(),
  };
};

const getColumns = () => {
  return [
    {
      accessorKey: 'itemName',
      header: () => <span>Item Name</span>,
      defaultSortIcon: true,
      enableSorting: true,
      cell: (value) => <span>{value.getValue()}</span>,
    },
    {
      accessorKey: 'itemDescription',
      header: () => <span>Description</span>,
      cell: (value) => <span>{value.getValue() || '-'}</span>,
    },
    {
      accessorKey: 'hsnCode',
      header: () => <span>HSN Code</span>,
      cell: (value) => <span>{value.getValue()}</span>,
    },
    {
      accessorKey: 'itemUnit',
      header: () => <span>Unit</span>,
      cell: (value) => <span>{value.getValue()}</span>,
    },
    {
      accessorKey: 'totalQuantity',
      header: () => <span>Total Qty</span>,
      enableSorting: true,
      cell: (value) => <span>{value.getValue()}</span>,
    },
    {
      accessorKey: 'totalAmount',
      header: () => <span>Subtotal</span>,
      enableSorting: true,
      cell: ({ getValue }) => <AmountCell value={getValue()} />,
    },
    {
      accessorKey: 'totalTax',
      header: () => <span>GST</span>,
      enableSorting: true,
      cell: ({ getValue }) => <AmountCell value={getValue()} />,
    },
    {
      accessorKey: 'grandTotal',
      header: () => <span>Total</span>,
      enableSorting: true,
      cell: ({ getValue }) => <AmountCell value={getValue()} />,
    },
  ];
};

const ByPRPO = (props) => {
  const { reportType, userType } = props;
  const [sorting, setSorting] = useState([{ id: 'totalQuantity', desc: true }]);
  const [pagination, setPagination] = useState({
    pageIndex: 0,
    pageSize: defaultPageSize,
  });
  const [total, setTotal] = useState(0);
  const [rows, setRows] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const defaultDateRange = useRef(getDefaultYearDateRange());
  const [date, setDate] = useState(() => defaultDateRange.current);
  const [isExporting, setExporting] = useState(false);
  const skipDateEffect = useRef(true);

  const effectCalled = useRef(false);

  const breadcrumbItems =
    reportType === 'PO'
      ? [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'By Item PO', link: '#' },
      ]
      : [
        { title: 'Dashboard', link: '/dashboard' },
        { title: 'By Item PR', link: '#' },
      ];

  useEffect(() => {
    if (!effectCalled.current) {
      props.setBreadcrumbItems(
        reportType === 'PO' ? 'By Item PO' : 'By Item PR',
        breadcrumbItems,
      );
      effectCalled.current = true;
    }
  }, []);

  const getItemPRPOList = async (page, limit, isExport = false) => {
    const sort = sorting[0] || { id: 'totalQuantity', desc: true };

    const dateRange = date || defaultDateRange.current;

    const body = {
      page: isExport ? 1 : page,
      limit: isExport ? 1000000 : limit,
      sortOrder: sort.desc ? 'desc' : 'asc',
      sortBy: sort.id || 'totalQuantity',
      clientId:
        userType === 'client'
          ? JSON.parse(localStorage.getItem('user'))?.clientId
          : '',
      startDate: dateRange.start,
      endDate: dateRange.end,
    };

    return reportType === 'PO'
      ? await getItemPOList(body)
      : await getItemPRList(body);
  };

  const fetchData = async (isExport = false) => {
    try {
      if (isExport) {
        setExporting(true);
      } else {
        setIsLoading(true);
      }

      const res = await getItemPRPOList(
        pagination.pageIndex + 1,
        pagination.pageSize,
        isExport,
      );

      if (res.success) {
        if (isExport) {
          return res.payload.data;
        } else {
          setRows(res.payload.data);
          setTotal(res.payload.totalCount);
        }
      }
      return [];
    } catch (err) {
      console.error(err);
      if (isExport) {
        toast.error('Failed to fetch data for export');
      }
      return [];
    } finally {
      if (isExport) {
        setExporting(false);
      } else {
        setIsLoading(false);
      }
    }
  };

  useEffect(() => {
    if (skipDateEffect.current) {
      skipDateEffect.current = false;
      return;
    }

    fetchData(false);
  }, [pagination, sorting, date]);

  const columns = useMemo(() => getColumns(reportType, userType), [reportType]);

  const resetPagination = () => {
    setPagination({
      pageIndex: 0,
      pageSize: defaultPageSize,
    });
  };

  const resetFilters = () => {
    setDate(defaultDateRange.current);
    setSorting([{ id: 'totalQuantity', desc: true }]);
    resetPagination();
  };

  const onDateChange = (data) => {
    skipDateEffect.current = false;
    setDate(data);
  };

  const downloadItemsExcel = async () => {
    try {
      const data = await fetchData(true);

      if (data.length > 0) {
        const flatItems = data.map((ele) => ({
          'Item Name': ele.itemName || '-',
          'Item Description': ele.itemDescription || '-',
          HSN: ele.hsnCode || '-',
          Unit: ele.itemUnit || '-',
          'Total Quantity': ele.totalQuantity || '-',
          'Total Amount': ele.totalAmount || '-',
          'Total Tax': ele.totalTax || '-',
          'Grand Total ': ele.grandTotal || '-',
        }));

        const worksheet = XLSX.utils.json_to_sheet(flatItems);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, 'PR_PO');

        XLSX.writeFile(
          workbook,
          reportType === 'PO' ? 'PO-Item-List.xlsx' : 'PR-Item-List.xlsx',
        );
        toast.success('Item(s) Exported Successfully!');
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
      toast.error('Error exporting data');
    }
  };

  return (
    <div>
      <div className="filter-wrapper">
        <div className="filter-toolbar">
          {/* LEFT SIDE */}
          <div className="filter-row">
            <DateFilter
              onDateChange={onDateChange}
              initialValue={defaultDateRange.current}
            />

            <Button
              variant="outlined"
              className="reset-btn"
              onClick={resetFilters}
            >
              Reset
            </Button>
          </div>

          {/* RIGHT SIDE */}
          <StyledButton
            color="primary"
            className="export-btn"
            onClick={downloadItemsExcel}
            isLoading={isExporting}
            disabled={isLoading}
          >
            <div className="export-btn-content">
              <i className="ion ion-md-cloud-download mx-2"></i>
              Export
            </div>
          </StyledButton>
        </div>
      </div>
      <Table
        data={rows}
        columns={columns}
        manualPagination
        rowCount={total}
        sorting={sorting}
        pagination={pagination}
        setSorting={setSorting}
        setPagination={setPagination}
        isLoading={isLoading}
        defaultSorting={[{ id: 'totalQuantity', desc: true }]}
      />
    </div>
  );
};

export default connect(null, { setBreadcrumbItems })(ByPRPO);
